import { useSettingsStore } from '../../lib/stores/settingsStore';
import { dealFulfillmentState, type DealFulfillment, type DealPendingSummary } from '../../lib/services/dealOrderService';
import { formatCurrency } from '../../lib/utils/formatters';

/**
 * Evasione di un'offerta accettata: quanto delle righe prodotto e' gia'
 * diventato ordine di magazzino. Non compare per offerte senza righe prodotto
 * o senza dati (magazzino non attivo, flusia-core non raggiungibile).
 */

const CONFIG: Record<DealFulfillment, { it: string; en: string; className: string; dot: string }> = {
  none: {
    it: 'Da evadere',
    en: 'Not fulfilled',
    className: 'bg-amber-50 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300',
    dot: '#CB912F',
  },
  partial: {
    it: 'Parzialmente evasa',
    en: 'Partially fulfilled',
    className: 'bg-blue-50 text-blue-700 dark:bg-blue-900/25 dark:text-blue-300',
    dot: '#2383E2',
  },
  full: {
    it: 'Evasa',
    en: 'Fulfilled',
    className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300',
    dot: '#0F7B6C',
  },
};

interface DealFulfillmentBadgeProps {
  summary?: DealPendingSummary;
  /** sm: per i chip delle card kanban */
  size?: 'sm' | 'md';
}

export function DealFulfillmentBadge({ summary, size = 'md' }: DealFulfillmentBadgeProps) {
  const language = useSettingsStore((s) => s.settings?.language);
  const state = dealFulfillmentState(summary);
  if (!state || !summary) return null;

  // Stessa lingua dell'etichetta di stato accanto (translations[language || 'en'])
  const it = language === 'it';
  const config = CONFIG[state];

  const parti: string[] = [];
  if (summary.ordered_lines !== undefined) {
    parti.push(
      it
        ? `${summary.ordered_lines} ${summary.ordered_lines === 1 ? 'riga prodotto' : 'righe prodotto'} su ${summary.product_lines} negli ordini`
        : `${summary.ordered_lines} of ${summary.product_lines} product lines in orders`,
    );
  } else if (summary.remaining_lines > 0) {
    parti.push(
      it
        ? `${summary.remaining_lines} ${summary.remaining_lines === 1 ? 'riga prodotto' : 'righe prodotto'} ancora da ordinare`
        : `${summary.remaining_lines} product lines still to order`,
    );
  }
  if (summary.total_value !== undefined && summary.ordered_value !== undefined && summary.total_value > 0) {
    parti.push(
      it
        ? `ordinato ${formatCurrency(summary.ordered_value)} su ${formatCurrency(summary.total_value)}`
        : `ordered ${formatCurrency(summary.ordered_value)} of ${formatCurrency(summary.total_value)}`,
    );
  }

  return (
    <span
      title={parti.join(' · ') || undefined}
      className={`inline-flex items-center gap-1 ${size === 'sm' ? 'text-[10px]' : 'text-[11px]'} font-medium px-1.5 py-0.5 rounded leading-none w-fit whitespace-nowrap ${config.className}`}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: config.dot }} />
      {it ? config.it : config.en}
    </span>
  );
}

export default DealFulfillmentBadge;
